import {
  IonBadge,
  IonCheckbox,
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPopover,
  IonRadio,
  IonRadioGroup
} from "@ionic/react";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {checkmarkCircleOutline, closeOutline} from "ionicons/icons";
import {styled} from "styled-components";
import {z} from "zod";
import {IonRowButton} from "app/employee/IonRowButton";
import {DateDisplay} from "app/employee/DateDisplay";
import type {OcrSuggestionPopoverProps} from "app/components/hooks/useOcrSuggestion";

const supplierChoiceSchema = z.enum(["existing", "new"]);

const FullWidthPopover = styled(IonPopover)`
  --width: 90%;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 0.75rem;
`;

const IndentedItem = styled(IonItem)`
  --padding-start: 2rem;
  font-size: 0.9rem;
`;

const getAppCenter = () => {
  const br = document.querySelector("ion-app")?.getBoundingClientRect();
  if (br) {
    return [br.width / 2, br.height / 2];
  } else {
    return [0, 0];
  }
};

export const OcrSuggestionPopover = ({
  isOpen,
  onClose,
  suggestion,
  applyFields,
  toggleApplySupplier,
  setSupplierChoice,
  toggleApplyField,
  supplierDisplay,
  handleApplySelected,
  hasNoResults
}: OcrSuggestionPopoverProps) => {
  const {t} = useTranslation();

  const event = useMemo(() => {
    if (isOpen) {
      return new MouseEvent("click", {
        clientX: getAppCenter()[0],
        clientY: 16
      });
    } else {
      return null;
    }
  }, [isOpen]);

  if (hasNoResults) {
    return (
      <FullWidthPopover
        alignment="center"
        event={event}
        isOpen={isOpen}
        onDidDismiss={onClose}
        reference="event"
        side="bottom"
        size="auto"
      >
        <IonContent>
          <IonList>
            <IonItem lines="full">
              <IonLabel className="ion-text-wrap">
                {t("incdoc.doc.ai-no-results", {
                  defaultValue:
                    "Не успяхме да разпознаем данни от документите. Моля, направете по-ясна снимка — уверете се, че има достатъчно светлина и целият документ се вижда в кадъра."
                })}
              </IonLabel>
            </IonItem>
          </IonList>
          <ButtonRow>
            <IonRowButton fill="outline" onClick={onClose}>
              <IonIcon icon={closeOutline} slot="start" />
              {t("g.close", {defaultValue: "Затвори"})}
            </IonRowButton>
          </ButtonRow>
        </IonContent>
      </FullWidthPopover>
    );
  }

  const hasSupplier = !!supplierDisplay.checkboxText;

  return (
    <FullWidthPopover
      alignment="center"
      event={event}
      isOpen={isOpen}
      onDidDismiss={onClose}
      reference="event"
      side="bottom"
      size="auto"
    >
      <IonContent>
        <IonList>
          <IonItem lines="full">
            <IonLabel>
              <strong>
                {t("incdoc.doc.ai-dialog-title", {defaultValue: "Сверете разпознатите атрибути с документа"})}
              </strong>
            </IonLabel>
          </IonItem>
          {hasSupplier && (
            <>
              <IonItem
                lines={supplierDisplay.matchKind === "FuzzyMatch" && applyFields.applySupplier ? "none" : "full"}
              >
                <IonCheckbox
                  checked={applyFields.applySupplier}
                  justify="start"
                  labelPlacement="end"
                  onIonChange={(e) => toggleApplySupplier(e.detail.checked)}
                >
                  {t("incdoc.doc.supplier", {defaultValue: "Доставчик"})}
                  <br />
                  <strong>{supplierDisplay.checkboxText}</strong>{" "}
                  {supplierDisplay.matchKind === "NotFound" && (
                    <IonBadge color="primary">{t("incdoc.doc.ai-new", {defaultValue: "Нов"})}</IonBadge>
                  )}
                  {supplierDisplay.matchKind === "ExactMatch" && (
                    <IonBadge color="medium">{t("incdoc.doc.ai-existing", {defaultValue: "Съществуващ"})}</IonBadge>
                  )}
                </IonCheckbox>
              </IonItem>
              {supplierDisplay.matchKind === "FuzzyMatch" && applyFields.applySupplier && (
                <IonRadioGroup
                  onIonChange={(e) => setSupplierChoice(supplierChoiceSchema.parse(e.detail.value))}
                  value={applyFields.supplierChoice}
                >
                  {supplierDisplay.newSupplierText && (
                    <IndentedItem lines={supplierDisplay.existingSupplierText ? "none" : "full"}>
                      <IonRadio justify="start" labelPlacement="end" value="new">
                        {t("incdoc.doc.ai-create-new", {defaultValue: "Създай нов"})}
                        <br />
                        <strong>{supplierDisplay.newSupplierText}</strong>
                      </IonRadio>
                    </IndentedItem>
                  )}
                  {supplierDisplay.existingSupplierText && (
                    <IndentedItem lines="full">
                      <IonRadio justify="start" labelPlacement="end" value="existing">
                        {t("incdoc.doc.ai-use-existing", {defaultValue: "Използвай съществуващ"})}
                        <br />
                        <strong>{supplierDisplay.existingSupplierText}</strong>
                      </IonRadio>
                    </IndentedItem>
                  )}
                </IonRadioGroup>
              )}
            </>
          )}
          {suggestion?.purchaseDocument?.invoiceNumber && (
            <IonItem lines="full">
              <IonCheckbox
                checked={applyFields.docNumber}
                justify="start"
                labelPlacement="end"
                onIonChange={(e) => toggleApplyField("docNumber", e.detail.checked)}
              >
                {t("incdoc.doc.doc-num", {defaultValue: "Номер на документ"})} <br />
                <strong>{suggestion.purchaseDocument.invoiceNumber}</strong>
              </IonCheckbox>
            </IonItem>
          )}
          {suggestion?.purchaseDocument?.date && (
            <IonItem lines="full">
              <IonCheckbox
                checked={applyFields.docDate}
                justify="start"
                labelPlacement="end"
                onIonChange={(e) => toggleApplyField("docDate", e.detail.checked)}
              >
                {t("incdoc.doc.doc-date", {defaultValue: "Дата на документа"})} <br />
                <strong>
                  <DateDisplay dt={suggestion.purchaseDocument.date} />
                </strong>
              </IonCheckbox>
            </IonItem>
          )}
        </IonList>
        <ButtonRow>
          <IonRowButton fill="outline" onClick={onClose}>
            <IonIcon icon={closeOutline} slot="start" />
            {t("g.cancel", {defaultValue: "Откажи"})}
          </IonRowButton>
          <IonRowButton onClick={handleApplySelected}>
            <IonIcon icon={checkmarkCircleOutline} slot="start" />
            {t("incdoc.doc.ai-apply-button", {defaultValue: "Попълни избраните"})}
          </IonRowButton>
        </ButtonRow>
      </IonContent>
    </FullWidthPopover>
  );
};
